/**
 * Stripe Error Mapper
 * Maps Stripe error codes to user-friendly messages with actionable guidance
 */

export const stripeErrorMap = {
  // Card errors
  'card_declined': {
    title: 'Card Declined',
    message: 'Your card was declined by your bank.',
    guidance: 'Please try a different payment method or contact your bank for more information.',
    severity: 'error',
    retry: true
  },
  'insufficient_funds': {
    title: 'Insufficient Funds',
    message: 'Your card has insufficient funds to complete this transaction.',
    guidance: 'Please use a different card or add funds to your account.',
    severity: 'error',
    retry: true
  },
  'expired_card': {
    title: 'Card Expired',
    message: 'Your card has expired.',
    guidance: 'Please use a different card or update your card details with your bank.',
    severity: 'error',
    retry: true
  },
  'incorrect_cvc': {
    title: 'Incorrect CVC',
    message: 'The security code (CVC) you entered is incorrect.',
    guidance: 'Please check the 3-digit code on the back of your card and try again.',
    severity: 'warning',
    retry: true
  },
  'incorrect_number': {
    title: 'Invalid Card Number',
    message: 'The card number you entered is invalid.',
    guidance: 'Please check your card number and try again.',
    severity: 'warning',
    retry: true
  },
  'invalid_expiry_month': {
    title: 'Invalid Expiry Month',
    message: 'The expiration month is invalid.',
    guidance: 'Please check your card\'s expiration date and try again.',
    severity: 'warning',
    retry: true
  },
  'invalid_expiry_year': {
    title: 'Invalid Expiry Year',
    message: 'The expiration year is invalid.',
    guidance: 'Please check your card\'s expiration date and try again.',
    severity: 'warning',
    retry: true
  },
  'processing_error': {
    title: 'Processing Error',
    message: 'An error occurred while processing your card.',
    guidance: 'Please try again in a few moments or use a different payment method.',
    severity: 'error',
    retry: true
  },
  
  // Authentication errors
  'authentication_required': {
    title: 'Authentication Required',
    message: 'Your bank requires additional authentication.',
    guidance: 'Please complete the verification with your bank to continue.',
    severity: 'warning',
    retry: false
  },
  'card_not_supported': {
    title: 'Card Not Supported',
    message: 'This card type is not supported for this transaction.',
    guidance: 'Please use a different card (Visa, Mastercard, or American Express).',
    severity: 'error',
    retry: true
  },
  
  // Fraud and security errors
  'fraudulent': {
    title: 'Payment Blocked',
    message: 'This payment was flagged by our security system.',
    guidance: 'Please contact support or try a different payment method.',
    severity: 'error',
    retry: false,
    fraud: true
  },
  'do_not_honor': {
    title: 'Payment Declined',
    message: 'Your card issuer declined this payment.',
    guidance: 'Please contact your bank for more information or use a different card.',
    severity: 'error',
    retry: true
  },
  'lost_card': {
    title: 'Card Reported Lost',
    message: 'This card has been reported as lost.',
    guidance: 'Please use a different payment method.',
    severity: 'error',
    retry: true,
    fraud: true
  },
  'stolen_card': {
    title: 'Card Reported Stolen',
    message: 'This card has been reported as stolen.',
    guidance: 'Please use a different payment method.',
    severity: 'error',
    retry: true,
    fraud: true
  },
  'pickup_card': {
    title: 'Card Not Available',
    message: 'Your card issuer has requested that you contact them.',
    guidance: 'Please contact your bank immediately.',
    severity: 'error',
    retry: false,
    fraud: true
  },
  'restricted_card': {
    title: 'Card Restricted',
    message: 'Your card has restrictions that prevent this transaction.',
    guidance: 'Please contact your bank or use a different card.',
    severity: 'error',
    retry: true
  },
  
  // Rate limit errors
  'rate_limit': {
    title: 'Too Many Attempts',
    message: 'You\'ve made too many payment attempts.',
    guidance: 'Please wait a few minutes before trying again.',
    severity: 'warning',
    retry: false
  },
  
  // Generic errors
  'generic_decline': {
    title: 'Payment Declined',
    message: 'Your card was declined.',
    guidance: 'Please try a different payment method or contact your bank.',
    severity: 'error',
    retry: true
  },
  'invalid_account': {
    title: 'Invalid Account',
    message: 'The card account is invalid.',
    guidance: 'Please use a different card.',
    severity: 'error',
    retry: true
  },
  'currency_not_supported': {
    title: 'Currency Not Supported',
    message: 'Your card doesn\'t support this currency.',
    guidance: 'Please use a different card that supports this currency.',
    severity: 'error',
    retry: true
  },
  
  // Network errors
  'network_error': {
    title: 'Connection Error',
    message: 'Unable to connect to the payment network.',
    guidance: 'Please check your internet connection and try again.',
    severity: 'error',
    retry: true
  },
  'api_error': {
    title: 'Service Error',
    message: 'Our payment service is temporarily unavailable.',
    guidance: 'Please try again in a few moments.',
    severity: 'error',
    retry: true
  }
};

/**
 * Get user-friendly error message from Stripe error
 * @param {Object} error - Stripe error object
 * @returns {Object} - Formatted error with title, message, guidance, and retry flag
 */
export const getStripeErrorMessage = (error) => {
  if (!error) {
    return {
      title: 'Payment Error',
      message: 'An unexpected error occurred.',
      guidance: 'Please try again or contact support.',
      severity: 'error',
      retry: true
    };
  }

  // Check if it's a Stripe error with a decline code
  const declineCode = error.decline_code || error.code;
  
  // Try to map by decline code first
  if (declineCode && stripeErrorMap[declineCode]) {
    return {
      ...stripeErrorMap[declineCode],
      originalError: error.message
    };
  }

  // Try to map by error type
  const errorType = error.type;
  if (errorType === 'card_error') {
    return {
      title: 'Card Error',
      message: error.message || 'There was an issue with your card.',
      guidance: 'Please check your card details and try again.',
      severity: 'error',
      retry: true,
      originalError: error.message
    };
  }

  if (errorType === 'validation_error') {
    return {
      title: 'Invalid Information',
      message: error.message || 'The information provided is invalid.',
      guidance: 'Please check your details and try again.',
      severity: 'warning',
      retry: true,
      originalError: error.message
    };
  }

  // Default fallback
  return {
    title: 'Payment Failed',
    message: error.message || 'Unable to process your payment.',
    guidance: 'Please try again or use a different payment method. If the problem persists, contact support.',
    severity: 'error',
    retry: true,
    originalError: error.message
  };
};

/**
 * Check if error indicates fraud
 * @param {Object} error - Stripe error object
 * @returns {boolean}
 */
export const isFraudError = (error) => {
  if (!error) return false;
  
  const declineCode = error.decline_code || error.code;
  const errorInfo = stripeErrorMap[declineCode];
  
  return errorInfo?.fraud === true;
};

/**
 * Get retry recommendation
 * @param {Object} error - Stripe error object
 * @returns {boolean}
 */
export const shouldRetryPayment = (error) => {
  if (!error) return true;
  
  const declineCode = error.decline_code || error.code;
  const errorInfo = stripeErrorMap[declineCode];
  
  return errorInfo?.retry !== false;
};

/**
 * Format error for display in UI
 * @param {Object} error - Stripe error object
 * @returns {string}
 */
export const formatErrorForDisplay = (error) => {
  const errorInfo = getStripeErrorMessage(error);
  return `${errorInfo.title}: ${errorInfo.message} ${errorInfo.guidance}`;
};
